// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// import SidebarUserSearch from './SidebarUserSearch';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';
import PostNewsModal from './postNewsModal';
import { requestNotificationPermission } from '../utils/fcm';
import { collection, query, where, onSnapshot, orderBy, limit, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase-config';
import './header.css'; // We will create new styles here

// You can use Font Awesome icons or simple text/emojis
const MenuIcon = () => <i className="fas fa-bars"></i>;
const LightModeIcon = () => <span>☀️</span>;
const DarkModeIcon = () => <span>🌙</span>;


const Header = () => {
  const { currentUser, logout, isAdmin, isBusiness } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPostNewsModal, setShowPostNewsModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [aiDraftData, setAiDraftData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userStats, setUserStats] = useState({ coins: 0, walletBalance: 0 });

  // Real-time listener for notifications
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, "notifications"),
      where("toUid", "==", currentUser.uid),
      orderBy("timestamp", "desc"),
      limit(15)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(items);
      setUnreadCount(items.filter(i => !i.read).length);
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
  if (!currentUser) return;
  const unsubscribe = onSnapshot(doc(db, "users", currentUser.uid), (doc) => {
    if (doc.exists()) {
      setUserStats(doc.data());
    }
  });
  return () => unsubscribe();
}, [currentUser]);

  const handleNotifClick = async (notif) => {
    await updateDoc(doc(db, "notifications", notif.id), { read: true });
    setIsMenuOpen(false);
    navigate(`/?openComments=${notif.postId}`);
  };

  useEffect(() => {
    const handleAIEvent = (event) => {
        setAiDraftData(event.detail); // This catches {title, description}
        setShowPostNewsModal(true);   // Automatically opens the modal
    };

    window.addEventListener('openPostModalWithAI', handleAIEvent);
    return () => window.removeEventListener('openPostModalWithAI', handleAIEvent);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setIsMenuOpen(false); // Close menu on logout
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.body.classList.toggle('dark-mode', !isDarkMode);
  };

  const handleNotificationClick = () => {
    if (currentUser) {
      // We only call the permission function when the button is clicked
      requestNotificationPermission(currentUser);
    } else {
      // If the user is not logged in, prompt them to log in first
      alert("Please log in to enable notifications.");
    }
  };

  return (
    <>
      {/* --- 1. THE HEADER BAR --- */}
      <header className="header">
        <div className="header-left">
          {/* REMOVED currentUser && - Menu button now always visible */}
          <button 
          id="tour-menu"
          className="header-icon-btn menu-button" onClick={() => setIsMenuOpen(true)}>
            <i className="fas fa-bars"></i>
          </button>
          
          <Link to="/" className="logo-container">
            <img src="/images/logo.png" alt="Ai News Logo" className="logo" />
            <h1 className="title">Ai News</h1>
          </Link>
        </div>

        <div className="header-right">
          {/* News can be posted by anyone logged in */}
          {currentUser && (
            <button className="header-button post-news-btn" onClick={() => setShowPostNewsModal(true)}>
              <i className="fas fa-edit"></i> <span className="button-text">Post News</span>
            </button>
          )}

          {/* 2. ONLY SHOW JOB LINK - BUT RESTRICT POSTING INSIDE THE JOB PAGE */}
          <Link to="/jobs" className="header-button jobs-btn">
             <i className="fas fa-briefcase"></i> <span className="button-text">Jobs</span>
          </Link>

          {!currentUser && (
            <button className="header-button login-btn" onClick={() => setShowLoginModal(true)}>
              Login
            </button>
          )}
        </div>
      </header>

      {/* --- 2. THE SIDE MENU (NOW ACCESSIBLE TO EVERYONE) --- */}
      <div className={`side-menu-overlay ${isMenuOpen ? 'open' : ''}`} onClick={() => setIsMenuOpen(false)}></div>
      <div className={`side-menu ${isMenuOpen ? 'open' : ''}`}>
        <div className="menu-header">
          <h3>{currentUser ? 'Welcome to Ai News' : 'Welcome to Ai News'}</h3>
          <button onClick={() => setIsMenuOpen(false)} className="close-menu-btn">&times;</button>
        </div>
        
        <nav className="menu-nav">
          <Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link>
          {/* Professional search navigation tab looking exactly like existing tabs */}
          <Link to="/search-users" onClick={() => setIsMenuOpen(false)}>
              🔍 Search Users
          </Link>
          {currentUser ? (
            /* --- LINKS FOR LOGGED IN USERS --- */
            <>
              <Link to="/profile" onClick={() => setIsMenuOpen(false)}>Profile</Link>
              <Link to="/liked" onClick={() => setIsMenuOpen(false)}>Liked Posts</Link>
              <Link to="/saved" onClick={() => setIsMenuOpen(false)}>Saved Posts</Link>
              {/* Add special Business/Admin indicators if you want, but keep navigation clean */}
              {/* {isBusiness && <span className="role-tag">Business Account</span>} */}
            </>
          ) : (
            /* --- LINKS FOR GUESTS ONLY --- */
            <button className="menu-login-link" onClick={() => { setIsMenuOpen(false); setShowLoginModal(true); }} style={{ textAlign: 'left', background: 'none', border: 'none', padding: '15px 20px', fontSize: '16px', cursor: 'pointer', color: '#1a73e8' }}>
              Login / Sign Up
            </button>
          )}

          {/* --- COMMON LINKS (Always show these for Google Review) --- */}
          <div style={{ borderTop: '1px solid #eee', marginTop: '10px', paddingTop: '10px' }}>
          {/* <p>Contact Information :</p>
          <p>
        <strong>Email 📧: </strong> 
        <a 
          href="mailto:ainewstelugu@zohomail.in" 
          style={{ color: '#007bff', textDecoration: 'underline' }}
        >
          ainewstelugu@zohomail.in
        </a>
      </p>
      <p>
        <strong>Phone 📞: </strong> 
        <a 
          href="tel:+919700881889" 
          style={{ color: '#007bff', textDecoration: 'underline' }}
        >
          +91 9700881889
        </a>
      </p> */}

      <div className="user-earnings-summary" style={{ padding: '15px 20px', background: '#fff9c4', margin: '10px', borderRadius: '8px' }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <span>
          <i className="fas fa-coins" style={{ color: '#fbc02d', marginRight: '8px' }}></i>
          <strong>Coins:</strong> {userStats.coins || 0}
        </span>
        {/* Math: coins * 0.05 to show current value */}
        <span style={{ fontSize: '12px', color: '#666' }}>
            Current Value: <strong style={{ color: '#2e7d32' }}>₹{((userStats.coins || 0) * 0.05).toFixed(2)}</strong>
        </span>
    </div>
</div>
  
  <div style={{ padding: '10px 20px' }}>
    <Link 
      to="/about" 
      onClick={() => setIsMenuOpen(false)} 
      style={{ 
        display: 'block', 
        fontWeight: 'bold', 
        color: '#0e0d0d', 
        textDecoration: 'none',
        fontSize: '16px' 
      }}
    >
      Contact Us
    </Link>
  </div>

  <div style={{ padding: '10px 20px' }}>
    <Link 
      to="/privacy-policy" 
      onClick={() => setIsMenuOpen(false)} 
      style={{ 
        display: 'block', 
        fontWeight: 'bold', 
        color: '#1a73e8', 
        textDecoration: 'none',
        fontSize: '16px' 
      }}
    >
      Privacy Policy
    </Link>
  </div>
  <div style={{ padding: '10px 20px' }}>
    <Link 
      to="/terms" 
      onClick={() => setIsMenuOpen(false)} 
      style={{ 
        display: 'block', 
        fontWeight: 'bold', 
        color: '#d32f2f', 
        textDecoration: 'none',
        fontSize: '16px' 
      }}
    >
      TermsAndConditions
    </Link>
  </div>

</div>

{currentUser && (
    <div className="menu-notifications-section" style={{ borderTop: '1px solid #eee', marginTop: '10px' }}>
        <h4 style={{ padding: '15px 20px', margin: 0, fontSize: '16px', display: 'flex', justifyContent: 'space-between' }}>
            Notifications 
            {unreadCount > 0 && <span style={{ background: 'red', color: 'white', borderRadius: '50%', padding: '2px 8px', fontSize: '12px' }}>{unreadCount}</span>}
        </h4>
        <div className="notif-scroll-area" style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
                <p style={{ padding: '10px 20px', color: '#888', fontSize: '14px' }}>No new interactions</p>
            ) : (
                notifications.map(n => (
                    <div 
                        key={n.id} 
                        onClick={() => handleNotifClick(n)} 
                        className={`notif-item ${n.read ? 'read' : 'unread'}`} 
                        style={{ 
                            padding: '12px 20px', 
                            fontSize: '13px', 
                            borderBottom: '1px solid #f0f0f0', 
                            cursor: 'pointer',
                            backgroundColor: n.read ? 'transparent' : '#f0f7ff' 
                        }}
                    >
                        <strong>{n.fromName}</strong> {n.text}
                    </div>
                ))
            )}
        </div>
    </div>
)}

          {/* --- ADMIN & LOGOUT --- */}
          {currentUser && isAdmin && <Link to="/admin" onClick={() => setIsMenuOpen(false)}>Admin Dashboard</Link>}
          {currentUser && (
            <button className="logout-button" onClick={handleLogout} style={{ marginTop: '20px' }}>Logout</button>
          )}
        </nav>
      </div>

      {/* --- 3. MODALS --- */}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
      {showPostNewsModal && (
        <PostNewsModal 
            onClose={() => {
                setShowPostNewsModal(false);
                setAiDraftData(null); // Clear data when closed
            }} 
            aiDraft={aiDraftData} // PASS DATA TO MODAL
        />
      )}
    </>
  );
};

export default Header;